import { DotsCheckbox } from "dots-ui/dotsCheckbox";
import { FormikProps } from "formik";
import { useContext, useMemo } from "react";
import {
  AudiovisualReportRow,
  SubmitAudiovisualReport,
} from "../../../../../generated/data-contracts";
import {
  AvisEditableList,
  IAvisEditableListFieldConfig,
  IRowState,
} from "../../../../shared/components/editableList";
import {
  AvisDescriptiveText,
  AvisSection,
} from "../../../../shared/components/form";
import {
  AvisInputDecimal,
  AvisInputInt,
  AvisInputMaskTiming,
  AvisInputText,
} from "../../../../shared/components/inputs";
import { AvisContext } from "../../../../shared/context/AvisContext";

interface IReportAudioVisualFormProps {
  bag: FormikProps<SubmitAudiovisualReport>;
  bagField: keyof SubmitAudiovisualReport;
  disabled: boolean;
  loading: boolean;
  allowBlankReport: boolean | undefined;
}
export function ReportAudioVisualForm(
  props: Readonly<IReportAudioVisualFormProps>,
) {
  const { bag, bagField, allowBlankReport, disabled, loading } = props;
  const { res } = useContext(AvisContext);

  const hideClear = true;

  const rowInitValue = useMemo(() => {
    return {
      name: "",
    };
  }, []);

  const fieldCollection: IAvisEditableListFieldConfig<
    IRowState<AudiovisualReportRow>
  >[] = [
    {
      name: "nameLv",
      title: res("licensees_LicenseReports_type3_label_NameLv"),
      minWidth: 100,
      width: 300,
      renderField: (field, prefix, i, val, onChange) => {
        return (
          <AvisInputText
            name={`${prefix}.${i}.${field.name}`}
            value={val[field.name]?.toString()}
            maxLength={255}
            onChange={onChange}
            disabled={disabled}
            hideClear={hideClear}
          />
        );
      },
    },
    {
      name: "name",
      title: res("licensees_LicenseReports_type3_label_Name"),
      required: true,
      minWidth: 120,
      width: 300,
      renderField: (field, prefix, i, val, onChange) => {
        return (
          <AvisInputText
            name={`${prefix}.${i}.${field.name}`}
            value={val[field.name]?.toString()}
            maxLength={255}
            onChange={onChange}
            disabled={disabled}
            hideClear={hideClear}
            required={field.required}
          />
        );
      },
    },

    {
      name: "manufactureYear",
      title: res("licensees_LicenseReports_type3_label_ManufactureYear"),
      minWidth: 50,
      width: 80,
      renderField: (field, prefix, i, val, onChange) => {
        return (
          <AvisInputText
            name={`${prefix}.${i}.${field.name}`}
            value={val[field.name]?.toString()}
            maxLength={200}
            onChange={onChange}
            disabled={disabled}
            hideClear={hideClear}
          />
        );
      },
    },
    {
      name: "director",
      title: res("licensees_LicenseReports_type3_label_Director"),
      minWidth: 50,
      width: 150,
      renderField: (field, prefix, i, val, onChange) => {
        return (
          <AvisInputText
            name={`${prefix}.${i}.${field.name}`}
            value={val[field.name]?.toString()}
            maxLength={255}
            onChange={onChange}
            disabled={disabled}
            hideClear={hideClear}
          />
        );
      },
    },
    {
      name: "musicAuthor",
      title: res("licensees_LicenseReports_type3_label_MusicAuthor"),
      minWidth: 50,
      width: 150,
      renderField: (field, prefix, i, val, onChange) => {
        return (
          <AvisInputText
            name={`${prefix}.${i}.${field.name}`}
            value={val[field.name]?.toString()}
            maxLength={255}
            onChange={onChange}
            disabled={disabled}
            hideClear={hideClear}
          />
        );
      },
    },
    {
      name: "totalTiming",
      title: res("label_Timing"),
      minWidth: 50,
      width: 120,
      renderField: (field, prefix, i, val, onChange) => {
        return (
          <AvisInputMaskTiming
            name={`${prefix}.${i}.${field.name}`}
            value={val[field.name]?.toString()}
            disabled={disabled}
            hideClear={hideClear}
            onChange={onChange}
          />
        );
      },
    },
    {
      name: "ticketRevenue",
      title: res("licensees_LicenseReports_type3_label_TicketRevenue"),
      dataType: "Double",
      minWidth: 50,
      width: 80,
      renderField: (field, prefix, i, val, onChange) => {
        return (
          <AvisInputDecimal
            name={`${prefix}.${i}.${field.name}`}
            value={val[field.name]?.toString()}
            onChange={onChange}
            disabled={disabled}
            hideClear={hideClear}
          />
        );
      },
    },
    {
      name: "totalVisitors",
      title: res("licensees_LicenseReports_type3_label_TotalVisitors"),
      minWidth: 50,
      maxWidth: 120,
      renderField: (field, prefix, i, val, onChange) => {
        return (
          <AvisInputInt
            name={`${prefix}.${i}.${field.name}`}
            value={val[field.name]?.toString()}
            onChange={onChange}
            disabled={disabled}
            hideClear={hideClear}
          />
        );
      },
    },
  ];

  return (
    <AvisSection title={res("licensees_LicenseReports_label_SectionData")}>
      <AvisDescriptiveText textKey="portal_LIC_LicenseReportForm3" />
      {allowBlankReport && (
        <DotsCheckbox<SubmitAudiovisualReport>
          bag={bag}
          bagField="eventNotOccuredPortal"
          checkboxLabel={res(
            "licensees_LicenseReports_shared_label_EventNotOccured",
          )}
          disabled={disabled}
        />
      )}
      {!bag.values.eventNotOccuredPortal && (
        <AvisEditableList<SubmitAudiovisualReport, AudiovisualReportRow>
          bag={bag}
          bagField={bagField}
          initValue={rowInitValue}
          confirmBeforeDelete={(data) => {
            if (JSON.stringify(data) === JSON.stringify(rowInitValue))
              return undefined;

            return {
              content: res("message_DeleteRow"),
              mustShow: true,
            };
          }}
          fields={fieldCollection}
          disabled={disabled}
          loading={loading}
        />
      )}
    </AvisSection>
  );
}
